import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { meetingsAPI, peoplePlatformAPI, eventsAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import TableListCheckbox from '../components/TableListCheckbox';
import ModalShell from '../components/ModalShell';
import UiSurface from '../components/UiSurface';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { useIsMobileSurface } from '../hooks/useUiSurfaceVisible';
import { searchFieldsMatch } from '../utils/searchText';

const MeetingMinutes = () => {
  const [searchParams] = useSearchParams();
  const forcedEventId = (searchParams.get('event_id') || '').trim();
  const { hasPermission } = usePermissions();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [members, setMembers] = useState([]);
  const [membersMap, setMembersMap] = useState({});
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingsPage, setMeetingsPage] = useState(1);
  const [meetingsTotal, setMeetingsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const isMobileSurface = useIsMobileSurface();
  const canBulkSelectMeetings = hasPermission('meetings.delete') && !isMobileSurface;
  const [selectedMeetingIds, setSelectedMeetingIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    participants: [], // IDs des membres sélectionnés
    agenda: '',
    decisions: '',
    event_id: ''
  });

  const selectedContextEvent = useMemo(
    () => events.find((e) => e.id === forcedEventId) || null,
    [events, forcedEventId]
  );
  const isMeetingContext = (selectedContextEvent?.type || '') === 'meeting';
  const contentLabel = isMeetingContext ? 'procès-verbal' : 'rapport';

  useEffect(() => {
    loadMeetings();
  }, [meetingsPage, itemsPerPage]);

  useEffect(() => {
    filterMeetings();
  }, [meetings, searchQuery, selectedYear, forcedEventId]);

  useEffect(() => {
    setSelectedMeetingIds([]);
  }, [meetingsPage, searchQuery, selectedYear]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (meetingsPage - 1) * itemsPerPage
      };
      const [meetingsResponse, membersResponse, eventsResponse] = await Promise.all([
        meetingsAPI.getAll(params),
        peoplePlatformAPI.list({ limit: 2000, skip: 0 }),
        eventsAPI.getAll()
      ]);
      
      // Gérer les deux formats de réponse pour meetings
      let meetingsList = [];
      if (Array.isArray(meetingsResponse.data)) {
        meetingsList = meetingsResponse.data;
        setMeetingsTotal(meetingsResponse.data.length);
      } else {
        meetingsList = meetingsResponse.data.meetings || [];
        setMeetingsTotal(meetingsResponse.data.total || 0);
      }
      
      setMeetings(meetingsList);
      setFilteredMeetings(meetingsList);
      
      const membersList = mapPeopleListResponse(membersResponse.data);
      const eventsList = Array.isArray(eventsResponse.data) ? eventsResponse.data : (eventsResponse.data?.events || []);
      
      setMembers(membersList);
      setEvents(eventsList);
      
      // Créer un map des membres par ID pour accès rapide
      const map = {};
      membersList.forEach(member => {
        map[member.id] = member.name;
      });
      setMembersMap(map);
    } catch (err) {
      console.error('Failed to load meetings:', err);
      error('Erreur lors du chargement des procès-verbaux');
    } finally {
      setLoading(false);
    }
  };

  const filterMeetings = () => {
    let filtered = meetings;

    if (forcedEventId) {
      filtered = filtered.filter((m) => String(m.event_id || '') === forcedEventId);
    }

    // Filtre par année
    if (selectedYear !== 'all') {
      filtered = filtered.filter(m => {
        const meetingYear = new Date(m.date).getFullYear().toString();
        return meetingYear === selectedYear;
      });
    }

    // Recherche par titre ou localisation
    if (searchQuery.trim()) {
      filtered = filtered.filter((m) =>
        searchFieldsMatch(searchQuery, [m.title, m.location])
      );
    }

    // Trier par date (plus récent en premier)
    filtered = filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredMeetings(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        participants: Array.isArray(formData.participants) ? formData.participants : [],
        agenda: formData.agenda.split('\n').filter(a => a.trim()),
        decisions: formData.decisions.split('\n').filter(d => d.trim()),
        event_id: formData.event_id || null
      };

      if (!data.event_id) {
        error('Sélectionnez un événement (réunion ou activité) pour rattacher le document.');
        return;
      }

      if (selectedMeeting) {
        const response = await meetingsAPI.update(selectedMeeting.id, data);
        // Si le PV est publié, la modification nécessite une approbation
        if (selectedMeeting.is_published) {
          success('Modification soumise pour approbation');
        } else {
          success('Procès-verbal mis à jour avec succès');
        }
      } else {
        await meetingsAPI.create(data);
        success('Procès-verbal créé avec succès');
      }
      
      loadMeetings();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save meeting:', err);
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde du procès-verbal');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Supprimer le procès-verbal',
      message: 'Êtes-vous sûr de vouloir supprimer ce procès-verbal ?',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await meetingsAPI.delete(id);
      success('Procès-verbal supprimé avec succès');
      loadMeetings();
    } catch (err) {
      console.error('Failed to delete meeting:', err);
      error(err.response?.data?.detail || 'Erreur lors de la suppression du procès-verbal');
    }
  };

  const handleSelectAllMeetings = (e) => {
    if (e.target.checked) {
      setSelectedMeetingIds(filteredMeetings.map((m) => m.id));
    } else {
      setSelectedMeetingIds([]);
    }
  };

  const toggleMeetingSelection = (id) => {
    setSelectedMeetingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkDeleteMeetings = async () => {
    const ids = selectedMeetingIds.filter((id) => filteredMeetings.some((m) => m.id === id));
    if (ids.length === 0) return;
    const ok = await confirm({
      title: 'Supprimer les procès-verbaux',
      message: `Supprimer ${ids.length} procès-verbal(aux) ? Cette action est irréversible.`,
      type: 'danger',
    });
    if (!ok) return;
    try {
      for (const id of ids) {
        await meetingsAPI.delete(id);
      }
      success(`${ids.length} procès-verbal(aux) supprimé(s).`);
      setSelectedMeetingIds([]);
      loadMeetings();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la suppression groupée.');
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      await meetingsAPI.submitForApproval(id);
      success('Procès-verbal soumis pour approbation');
      loadMeetings();
    } catch (err) {
      console.error('Failed to submit meeting:', err);
      error(err.response?.data?.detail || 'Erreur lors de la soumission du procès-verbal');
    }
  };

  const handleApproveMeeting = async (id) => {
    const confirmed = await confirm({
      title: 'Approuver le procès-verbal',
      message: 'Êtes-vous sûr de vouloir approuver ce procès-verbal ? Il sera publié et les membres recevront une notification.',
      type: 'info'
    });
    
    if (!confirmed) return;
    
    try {
      await meetingsAPI.approve(id);
      success('Procès-verbal approuvé et publié avec succès');
      loadMeetings();
    } catch (err) {
      console.error('Failed to approve meeting:', err);
      error(err.response?.data?.detail || 'Erreur lors de l\'approbation du procès-verbal');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      location: '',
      participants: [],
      agenda: '',
      decisions: '',
      event_id: forcedEventId || ''
    });
    setSelectedMeeting(null);
  };

  const handleEdit = (meeting) => {
    setSelectedMeeting(meeting);
    setFormData({
      ...meeting,
      participants: Array.isArray(meeting.participants) ? meeting.participants : [],
      agenda: Array.isArray(meeting.agenda) ? meeting.agenda.join('\n') : '',
      decisions: Array.isArray(meeting.decisions) ? meeting.decisions.join('\n') : '',
      event_id: meeting.event_id || ''
    });
    setShowForm(true);
  };

  const handleEventSelect = (eventId) => {
    if (!eventId) {
      setFormData({ ...formData, event_id: '', title: '', date: '', location: '' });
      return;
    }
    
    const selectedEvent = events.find(e => e.id === eventId);
    if (selectedEvent) {
      setFormData({
        ...formData,
        event_id: eventId,
        title: selectedEvent.title || formData.title,
        date: selectedEvent.start_date ? selectedEvent.start_date.split('T')[0] : formData.date,
        location: selectedEvent.location || formData.location
      });
    }
  };

  const handleParticipantToggle = (memberId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(memberId)
        ? prev.participants.filter(id => id !== memberId)
        : [...prev.participants, memberId]
    }));
  };

  const meetingsTotalPages = Math.max(1, Math.ceil(meetingsTotal / itemsPerPage));
  const renderMeetingsPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={meetingsPage}
      totalPages={meetingsTotalPages}
      totalItems={meetingsTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setMeetingsPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setMeetingsPage(1);
      }}
    />
  );

  const latestPublished = useMemo(() => {
    const published = (filteredMeetings || []).filter((m) => m?.is_published);
    if (!published.length) return null;
    return [...published].sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')))[0];
  }, [filteredMeetings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <MemberMobileShell>
        <MemberMobileHero
          label={isMeetingContext ? 'Procès-verbaux' : 'Rapports'}
          title={
            latestPublished
              ? latestPublished.title || `Dernier ${contentLabel}`
              : filteredMeetings.length
                ? `${filteredMeetings.length} ${contentLabel}(s)`
                : `Aucun ${contentLabel}`
          }
          hint={
            latestPublished?.date
              ? new Date(latestPublished.date).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : 'Documents liés aux réunions'
          }
          tone={latestPublished ? 'info' : 'neutral'}
          actions={
            <>
              {latestPublished ? (
                <Link
                  to={`/meetings/${latestPublished.id}`}
                  className="mm-cta flex-1"
                >
                  Ouvrir
                </Link>
              ) : null}
              {hasPermission('meetings.create') ? (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className={`mm-cta flex-1 ${
                    latestPublished ? 'mm-cta--ghost' : ''
                  }`}
                >
                  + Nouveau
                </button>
              ) : null}
            </>
          }
        />
      </MemberMobileShell>

      {/* Header */}
      <div className="hidden md:block">
      <PageHeader
        title={isMeetingContext ? 'Procès-verbal de réunion' : 'Rapports d’activité'}
        subtitle={
          forcedEventId
            ? `Document rattaché à l’événement sélectionné (${isMeetingContext ? 'réunion' : 'activité'})`
            : 'Rattachez chaque document à un événement du calendrier.'
        }
        actions={
          hasPermission('meetings.create') && (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap"
            >
              + Nouveau {contentLabel}
            </button>
          )
        }
      />
      </div>

      {canBulkSelectMeetings && selectedMeetingIds.length > 0 && (
        <div className="bg-entrea-panel rounded-lg shadow-sm p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-entrea-sky/40">
          <span className="text-sm text-gray-900 font-semibold flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-entrea-royal text-white flex items-center justify-center text-xs font-bold">
              {selectedMeetingIds.length}
            </div>
            {contentLabel}(s) sélectionné(s)
          </span>
          <button
            type="button"
            onClick={handleBulkDeleteMeetings}
            className="px-3 py-1.5 text-xs sm:text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition font-medium"
          >
            Supprimer
          </button>
        </div>
      )}

      <div className="grid gap-3 sm:gap-4">
        {meetings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 text-center border border-gray-200">
            <div className="inline-flex p-4 bg-entrea-panel rounded-full mb-4">
              <svg className="h-12 w-12 text-entrea-royal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun {contentLabel}</h3>
            <p className="text-gray-500 mb-6">Commencez par créer votre premier {contentLabel}</p>
            {hasPermission('meetings.create') && (
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="px-6 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium shadow-sm"
              >
                + Créer un {contentLabel}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Cards View */}
            <div className="md:hidden space-y-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {renderMeetingsPagination('top')}
              <div className="space-y-3 px-1">
              {filteredMeetings.map((meeting) => {
                const meetingDate = new Date(meeting.date);
                const isPublished = meeting.is_published;
                
                return (
                  <div key={meeting.id} className="flex gap-2 items-start">
                  {canBulkSelectMeetings && (
                    <TableListCheckbox
                      checked={selectedMeetingIds.includes(meeting.id)}
                      onChange={() => toggleMeetingSelection(meeting.id)}
                      size="lg"
                      className="mt-1 flex-shrink-0"
                      aria-label={`Sélectionner ${meeting.title}`}
                    />
                  )}
                  <Link
                    to={`/meetings/${meeting.id}`}
                    className="relative block flex-1 min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2">{meeting.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>{meetingDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                        {meeting.location && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{meeting.location}</span>
                          </div>
                        )}
                      </div>
                      <span className={`ml-3 px-4 py-2 text-xs font-bold rounded-full flex-shrink-0 shadow-lg ${
                        isPublished 
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' 
                          : meeting.pending_approval
                          ? 'bg-gradient-to-r from-blue-500 to-entrea-royal text-white'
                          : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                      }`}>
                        {isPublished ? '✓ Publié' : meeting.pending_approval ? '⏳ En attente' : '📝 Brouillon'}
                      </span>
                    </div>
                  </Link>
                  </div>
                );
              })}
              </div>
              {renderMeetingsPagination('bottom')}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {renderMeetingsPagination('top')}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canBulkSelectMeetings && (
                    <th className="px-4 sm:px-6 py-4 text-left w-12">
                      <TableListCheckbox
                        onChange={handleSelectAllMeetings}
                        checked={
                          filteredMeetings.length > 0 &&
                          selectedMeetingIds.length === filteredMeetings.length
                        }
                        aria-label="Tout sélectionner sur la page filtrée"
                      />
                    </th>
                  )}
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lieu</th>
                  {(hasPermission('meetings.edit') || hasPermission('meetings.delete')) && (
                    <th className="px-4 sm:px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMeetings.map((meeting) => {
                  const meetingDate = new Date(meeting.date);
                  const isPublished = meeting.is_published;
                  
                  return (
                    <tr key={meeting.id} className="hover:bg-gray-50 transition-colors">
                      {canBulkSelectMeetings && (
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <TableListCheckbox
                            checked={selectedMeetingIds.includes(meeting.id)}
                            onChange={() => toggleMeetingSelection(meeting.id)}
                            aria-label={`Sélectionner ${meeting.title}`}
                          />
                        </td>
                      )}
                      {/* Titre - cliquable */}
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/meetings/${meeting.id}`}
                          className="text-base font-semibold text-entrea-royal hover:text-entrea-navy hover:underline transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5 text-entrea-sky" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {meeting.title}
                        </Link>
                      </td>
                      
                      {/* Statut */}
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                          isPublished 
                            ? 'bg-green-100 text-green-700' 
                            : meeting.pending_approval
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {isPublished ? (
                            <>
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Publié
                            </>
                          ) : meeting.pending_approval ? (
                            <>
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              En attente
                            </>
                          ) : (
                            <>
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                    </svg>
                              Brouillon
                            </>
                          )}
                  </span>
                      </td>
                      
                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {meetingDate.toLocaleDateString('fr-FR', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                      </td>
                      
                      {/* Lieu */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                          <span className="truncate max-w-xs" title={meeting.location}>
                    {meeting.location}
                  </span>
                </div>
                      </td>
                      
                      {/* Actions */}
                      {(hasPermission('meetings.edit') || hasPermission('meetings.delete')) && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {/* Bouton Soumettre pour approbation */}
                            {!isPublished && !meeting.pending_approval && hasPermission('meetings.edit') && (
                              <button
                                onClick={() => handleSubmitForApproval(meeting.id)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Soumettre pour approbation"
                              >
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                            {/* Bouton Approuver */}
                            {meeting.pending_approval && hasPermission('meetings.publish') && (
                              <button
                                onClick={() => handleApproveMeeting(meeting.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Approuver et publier"
                              >
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                            {/* Bouton Modifier */}
                            {hasPermission('meetings.edit') && (
                <button
                  onClick={() => handleEdit(meeting)}
                                className="p-2 text-entrea-royal hover:bg-entrea-panel rounded-lg transition-colors"
                                title={isPublished ? "Modifier (nécessite approbation)" : "Modifier"}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                            )}
                            {/* Bouton Supprimer */}
                            {hasPermission('meetings.delete') && (
                <button
                  onClick={() => handleDelete(meeting.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
                            )}
              </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
              {renderMeetingsPagination('bottom')}
            </div>
          </>
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">Réunions</p>
              <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {selectedMeeting ? `Modifier le ${contentLabel}` : `Nouveau ${contentLabel}`}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label="Annuler"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
              {/* Sélection de l'événement/réunion programmée */}
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Réunion/Événement programmé
                </label>
                <select
                  value={formData.event_id}
                  onChange={(e) => handleEventSelect(e.target.value)}
                  className="mm-field"
                >
                  <option value="">-- Sélectionner un événement --</option>
                  {events
                    .filter((e) => !forcedEventId || e.id === forcedEventId)
                    .filter(e => !selectedMeeting || meetings.find(m => m.event_id === e.id && m.id === selectedMeeting.id) || !meetings.find(m => m.event_id === e.id))
                    .map(event => (
                      <option key={event.id} value={event.id}>
                        {event.title} ({event.type === 'meeting' ? 'réunion' : 'activité'}) - {new Date(event.start_date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                </select>
                <p className="mt-1.5 text-xs text-slate-500">
                  Chaque réunion doit produire un procès-verbal, et chaque activité un rapport.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Titre</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mm-field"
                  required
                  placeholder="Entrez le titre de la réunion"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mm-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Lieu</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mm-field"
                    required
                    placeholder="Entrez le lieu de la réunion"
                  />
                </div>
              </div>

              {/* Sélection des participants parmi les membres */}
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Participants ({formData.participants.length} sélectionné(s))
                </label>
                <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200/90 bg-white/70 p-3">
                  {members.length === 0 ? (
                    <p className="py-4 text-center text-sm text-slate-500">Aucun membre disponible</p>
                  ) : (
                    <div className="space-y-1">
                      {members.map(member => (
                        <label
                          key={member.id}
                          className="mm-press flex cursor-pointer items-center gap-3 rounded-xl p-2 hover:bg-slate-50"
                        >
                          <TableListCheckbox
                            checked={formData.participants.includes(member.id)}
                            onChange={() => handleParticipantToggle(member.id)}
                            aria-label={`Inclure ${member.name} comme participant`}
                          />
                          <div className="flex flex-1 items-center gap-2">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#edf4ff]">
                              <span className="text-xs font-bold text-[#113a5b]">
                                {member.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <span className="text-sm font-semibold text-[#0f3350]">{member.name}</span>
                              {member.role && (
                                <span className="ml-2 text-xs text-slate-500">({member.role})</span>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Ordre du jour (un point par ligne)</label>
                <textarea
                  value={formData.agenda}
                  onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                  className="mm-field"
                  rows={4}
                  placeholder="Point 1\nPoint 2\nPoint 3"
                />
              </div>

              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Décisions (une par ligne)</label>
                <textarea
                  value={formData.decisions}
                  onChange={(e) => setFormData({ ...formData, decisions: e.target.value })}
                  className="mm-field"
                  rows={4}
                  placeholder="Décision 1\nDécision 2\nDécision 3"
                />
              </div>
            </div>

            <div className="mm-modal-footer">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="mm-cta mm-cta--ghost flex-1"
              >
                Annuler
              </button>
              <button type="submit" className="mm-cta flex-1">
                {selectedMeeting ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

    </div>
  );
};

export default MeetingMinutes;